import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { _dataProvider } from '../dataProvider/DataProvider';

export interface IGroup {
  groupId: number;
  name: string;
  [key: string]: any;
}

interface IUseUserGroupsReturn {
  groupList: IGroup[];
  filteredGroups: IGroup[];
  txt: string;
  loading: boolean;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  reloadGroups: () => Promise<IGroup[]>;
}

/**
 * Custom hook for fetching and filtering user groups
 * Extracts common logic used by PromptPickGroup and PromptPickShareTarget
 */
export const useUserGroups = (): IUseUserGroupsReturn => {
  const [groupList, setGroups] = useState<IGroup[]>([]);
  const [txt, setTxt] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const { t } = useTranslation();
  const requestGenRef = useRef(0);

  const reloadGroups = useCallback(async (): Promise<IGroup[]> => {
    const gen = ++requestGenRef.current;
    setLoading(true);
    try {
      const groups: any = await _dataProvider.getUserGroups();
      if (gen !== requestGenRef.current) {
        return [];
      }
      if (!groups) {
        setTxt(t('GroupScreen.ServerError'));
        setGroups([]);
        return [];
      }
      if (groups.length === 0) {
        setTxt(t('NoGroup'));
        setGroups([]);
        return [];
      }

      const standardizedGroups: IGroup[] = groups.map((group: any) => ({
        ...group,
        name: group.name || group.Name || ''
      }));
      setGroups(standardizedGroups);
      setTxt('');
      return standardizedGroups;
    } finally {
      if (gen === requestGenRef.current) {
        setLoading(false);
      }
    }
  }, [t]);

  useEffect(() => {
    void reloadGroups();
    return () => {
      requestGenRef.current += 1;
    };
  }, [reloadGroups]);

  const filteredGroups = useMemo(
    () =>
      groupList.filter((item: IGroup) =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    [groupList, searchQuery]
  );

  return {
    groupList,
    filteredGroups,
    txt,
    loading,
    searchQuery,
    setSearchQuery,
    reloadGroups
  };
};

